import React, { useState, useEffect } from 'react';

/**
 * CookieConsent — banner zgodny z RODO/ePrivacy w stylu Terroir.
 *
 * - Pokazuje się 1.5s po wejściu jeśli brak akceptacji w localStorage
 * - Akceptacja zapisuje 'cookieConsent' + datę
 * - Odrzucenie zostawia użytkownika na stronie głównej (aplikacja
 *   wymaga ciasteczek httpOnly do logowania)
 * - Z założenia nie ma overlay'a blokującego całą aplikację — banner
 *   wjeżdża z dołu, można go obejrzeć i zaakceptować bez agresywnego
 *   modal-trapu (typowe dark-pattern jest zdrową informacją)
 */
export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) return;
    const timer = setTimeout(() => {
      setShowBanner(true);
      // małe opóźnienie żeby transformacja zadziałała
      requestAnimationFrame(() => setAnimateIn(true));
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setAnimateIn(false);
    setTimeout(() => setShowBanner(false), 250);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setAnimateIn(false);
    setTimeout(() => {
      setShowBanner(false);
      window.location.href = '/';
    }, 250);
  };

  if (!showBanner) return null;

  return (
    <div
      className="redesign-root"
      role="dialog"
      aria-modal="false"
      aria-label="Informacja o ciasteczkach"
      style={{
        position: 'fixed',
        left: 12, right: 12,
        bottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
        zIndex: 9000,
        display: 'flex', justifyContent: 'center',
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          maxWidth: 'min(560px, calc(100vw - 24px))',
          width: '100%',
          boxSizing: 'border-box',
          background: 'var(--t-bg-elev, #1a1f1c)',
          color: 'var(--t-text, #e8e6dd)',
          border: '1px solid var(--t-line, rgba(232, 230, 221, 0.10))',
          borderRadius: 14,
          padding: 'clamp(16px, 4vw, 22px)',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(168, 196, 158, 0.08)',
          fontFamily: "'DM Sans', sans-serif",
          transform: animateIn ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
          opacity: animateIn ? 1 : 0,
          transition: 'transform 320ms cubic-bezier(.2, .8, .2, 1), opacity 220ms ease',
          maxHeight: 'calc(100dvh - 24px)',
          overflowY: 'auto',
        }}
      >
        {/* Eyebrow */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          marginBottom: 14,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'rgba(168, 196, 158, 0.12)',
            display: 'grid', placeItems: 'center',
            fontSize: 15, lineHeight: 1,
          }}>🍪</div>
          <div style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 10,
            letterSpacing: '.22em',
            textTransform: 'uppercase',
            color: 'var(--t-sage, #a8c49e)',
          }}>Ciasteczka i prywatność</div>
        </div>

        {/* Title */}
        <div style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontSize: 24, lineHeight: 1.15,
          letterSpacing: '-.02em',
          color: 'var(--t-text, #e8e6dd)',
          marginBottom: 12,
        }}>Tylko niezbędne ciasteczka.</div>

        {/* Body */}
        <div style={{
          fontSize: 14, lineHeight: 1.6,
          color: 'var(--t-text-dim, rgba(232, 230, 221, 0.65))',
          marginBottom: 16,
        }}>
          Przechowujemy token sesji w bezpiecznym <code style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12, color: 'var(--t-sage, #a8c49e)',
            padding: '1px 6px', borderRadius: 4,
            background: 'rgba(168, 196, 158, 0.08)',
          }}>httpOnly cookie</code> oraz Twoje preferencje (motyw, język).
          Bez tego nie zalogujesz się do aplikacji.
        </div>

        {/* Mała lista co używamy */}
        <ul style={{
          margin: 0, padding: '12px 16px',
          listStyle: 'none',
          background: 'rgba(232, 230, 221, 0.025)',
          borderRadius: 10,
          fontSize: 12,
          color: 'var(--t-text-dim, rgba(232, 230, 221, 0.65))',
          lineHeight: 1.7,
          marginBottom: 18,
        }}>
          <li style={{ paddingLeft: 14, position: 'relative' }}>
            <span style={{
              position: 'absolute', left: 0,
              color: 'var(--t-sage, #a8c49e)',
            }}>·</span>
            <strong style={{ color: 'var(--t-text, #e8e6dd)', fontWeight: 500 }}>Token sesji</strong> — żebyś pozostał zalogowany
          </li>
          <li style={{ paddingLeft: 14, position: 'relative' }}>
            <span style={{
              position: 'absolute', left: 0,
              color: 'var(--t-sage, #a8c49e)',
            }}>·</span>
            <strong style={{ color: 'var(--t-text, #e8e6dd)', fontWeight: 500 }}>Preferencje</strong> — tryb ciemny, język
          </li>
          <li style={{ paddingLeft: 14, position: 'relative' }}>
            <span style={{
              position: 'absolute', left: 0,
              color: 'var(--t-sage, #a8c49e)',
            }}>·</span>
            <strong style={{ color: 'var(--t-text, #e8e6dd)', fontWeight: 500 }}>Zero śledzących</strong> — bez analityki, bez reklam
          </li>
        </ul>

        {/* Akcje */}
        <div style={{
          display: 'flex', gap: 10, flexWrap: 'wrap',
        }}>
          <button
            type="button"
            onClick={handleAccept}
            style={{
              flex: '1 1 auto', minWidth: 160,
              padding: '12px 20px',
              background: 'var(--t-sage, #a8c49e)',
              color: 'var(--t-bg, #0f1410)',
              border: 'none',
              borderRadius: 10,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14, fontWeight: 500,
              letterSpacing: '-.01em',
              cursor: 'pointer',
              transition: 'transform 150ms ease, background 200ms ease',
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
          >Akceptuję — wchodzę do ogrodu</button>
          <button
            type="button"
            onClick={handleDecline}
            style={{
              flex: '0 1 auto',
              padding: '12px 20px',
              background: 'transparent',
              color: 'var(--t-text-mute, rgba(232, 230, 221, 0.40))',
              border: '1px solid var(--t-line-2, rgba(232, 230, 221, 0.18))',
              borderRadius: 10,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              cursor: 'pointer',
              transition: 'border-color 200ms ease, color 200ms ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--t-rust, #c06a4a)';
              e.currentTarget.style.color = 'var(--t-rust, #c06a4a)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--t-line-2, rgba(232, 230, 221, 0.18))';
              e.currentTarget.style.color = 'var(--t-text-mute, rgba(232, 230, 221, 0.40))';
            }}
          >Odrzuć</button>
        </div>

        {/* Link do polityki */}
        <div style={{
          marginTop: 14, fontSize: 11,
          color: 'var(--t-text-mute, rgba(232, 230, 221, 0.40))',
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: '.06em',
          textAlign: 'center',
        }}>
          Pełna informacja w{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'var(--t-sage, #a8c49e)',
              textDecoration: 'none',
              borderBottom: '1px dotted currentColor',
            }}
          >polityce prywatności</a>
        </div>
      </div>
    </div>
  );
}
